import { TitleMonitoringConfig } from 'api/titleMonitoring/types';
import { DocumentType, newWindow, WindowFeatures } from 'features/constants';
import { useMarketPlace } from 'hooks/useMarketPlace';
import { ChangeEvent, RefObject, useEffect, useRef, useState } from 'react';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { openDocument } from 'utilities/utilities';

export interface TitleMonitoringConfiguration {
  logoUrl: string | null;
  includeCoverPage: boolean;
  setIncludeCoverPage: (checked: boolean) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  handleLogoUpload: (e: ChangeEvent<HTMLInputElement>) => void;
  handleLogoDelete: () => void;
  handleSave: () => Promise<void>;
  handleCancel: () => void;
  isDirty: boolean;
  isSaving: boolean;
  downloadFlyer: () => void;
  isFlyerLoading: boolean;
  canDownloadFlyer: boolean;
  createConfigIfMissing: () => Promise<void>;
}

/* Replacing a logo overwrites the same firm-keyed URL, so the browser would otherwise keep serving the old image */
const withCacheBuster = (url: string | null, cacheKey: number): string | null => {
  if (!url || url.startsWith('blob:')) {
    return url;
  }
  return `${url}${url.includes('?') ? '&' : '?'}v=${cacheKey}`;
};

export const useTitleMonitoringConfiguration = (
  titleMonitoringConfig?: TitleMonitoringConfig | null,
): TitleMonitoringConfiguration => {
  const { addSnackbarMessage } = useSnackBars();
  const {
    fetchTitleMonitoringFlyer,
    fetchTitleMonitoringFlyerResults,
    fetchTitleMonitoringFlyerStatus,
    saveTitleMonitoringConfig,
    saveTitleMonitoringConfigStatus,
    saveTitleMonitoringConfigResults,
  } = useMarketPlace();

  const [activeConfig, setActiveConfig] = useState<TitleMonitoringConfig | null>(titleMonitoringConfig ?? null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [savedLogoUrl, setSavedLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoCacheKey, setLogoCacheKey] = useState<number>(() => Date.now());
  const [includeCoverPage, setIncludeCoverPage] = useState(false);
  const [savedIncludeCoverPage, setSavedIncludeCoverPage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isCreatingConfigRef = useRef(false);

  const isLogoRemoved = !logoFile && !logoUrl && !!savedLogoUrl;
  const isDirty = logoUrl !== savedLogoUrl || includeCoverPage !== savedIncludeCoverPage;

  const revokeLocalPreview = () => {
    if (logoUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(logoUrl);
    }
  };

  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      revokeLocalPreview();
      setLogoFile(file);
      setLogoUrl(URL.createObjectURL(file));
    }
    e.target.value = '';
  };

  const handleLogoDelete = () => {
    revokeLocalPreview();
    setLogoFile(null);
    setLogoUrl(null);
  };

  const handleSave = async () => {
    await saveTitleMonitoringConfig({
      agentLogoUrl: savedLogoUrl,
      logoFile,
      removeLogo: isLogoRemoved,
      includeCoverPageInJacket: includeCoverPage,
    });
  };

  const handleCancel = () => {
    revokeLocalPreview();
    setLogoFile(null);
    setLogoUrl(savedLogoUrl);
    setIncludeCoverPage(savedIncludeCoverPage);
  };

  // Creating the config up front gives us the firmGuid needed for the rest of the setup
  const createConfigIfMissing = async () => {
    if (activeConfig) {
      return;
    }
    isCreatingConfigRef.current = true;
    await saveTitleMonitoringConfig({ agentLogoUrl: null, includeCoverPageInJacket: false });
  };

  const downloadFlyer = () => {
    if (activeConfig?.firmGuid && !isDirty) {
      fetchTitleMonitoringFlyer(activeConfig.firmGuid);
    }
  };

  useEffect(() => {
    if (titleMonitoringConfig) {
      setActiveConfig(titleMonitoringConfig);
    }
  }, [titleMonitoringConfig]);

  useEffect(() => {
    const savedLogo = activeConfig?.agentLogoUrl ?? null;
    setLogoUrl(savedLogo);
    setSavedLogoUrl(savedLogo);

    const savedCoverPage = activeConfig?.includeCoverPageInJacket ?? false;
    setIncludeCoverPage(savedCoverPage);
    setSavedIncludeCoverPage(savedCoverPage);
  }, [activeConfig?.agentLogoUrl, activeConfig?.includeCoverPageInJacket]);

  useEffect(() => {
    if (saveTitleMonitoringConfigStatus === 'success' && saveTitleMonitoringConfigResults) {
      const persistedLogo = saveTitleMonitoringConfigResults.agentLogoUrl ?? null;
      revokeLocalPreview();
      setLogoFile(null);
      setLogoUrl(persistedLogo);
      setSavedLogoUrl(persistedLogo);
      setLogoCacheKey(Date.now());
      setSavedIncludeCoverPage(saveTitleMonitoringConfigResults.includeCoverPageInJacket);
      setActiveConfig(saveTitleMonitoringConfigResults);
      if (isCreatingConfigRef.current) {
        isCreatingConfigRef.current = false;
      } else {
        addSnackbarMessage({
          message: 'Title Monitoring configuration saved',
          type: 'success',
        });
      }
    }
  }, [saveTitleMonitoringConfigStatus, saveTitleMonitoringConfigResults]);

  useEffect(() => {
    if (fetchTitleMonitoringFlyerResults?.fileContent) {
      openDocument(
        fetchTitleMonitoringFlyerResults.fileContent,
        DocumentType,
        newWindow,
        WindowFeatures,
        fetchTitleMonitoringFlyerResults.fileName,
      );
    }
  }, [fetchTitleMonitoringFlyerResults]);

  return {
    logoUrl: withCacheBuster(logoUrl, logoCacheKey),
    includeCoverPage,
    setIncludeCoverPage,
    fileInputRef,
    handleLogoUpload,
    handleLogoDelete,
    handleSave,
    handleCancel,
    isDirty,
    isSaving: saveTitleMonitoringConfigStatus === 'pending',
    downloadFlyer,
    isFlyerLoading: fetchTitleMonitoringFlyerStatus === 'pending',
    canDownloadFlyer: !!activeConfig?.firmGuid && !isDirty,
    createConfigIfMissing,
  };
};
